// AI Generated: Bus Operator Onboarding Home Page - Figma Design Implementation
import React, { useEffect, useCallback } from 'react';
import styles from '$styles/ind/home/styles.module.scss';
import Header from './subcomponents/Header';
import PlatformPartners from './subcomponents/PlatformPartners';
import Capabilities from './subcomponents/Capabilities';
import BusinessBenefits from './subcomponents/BusinessBenefits';
import Testimonials from './subcomponents/Testimonials';
import RegistrationCTA from './subcomponents/RegistrationCTA';
// AI Generated: Import shared FAQ section from registration (same design across all pages - Figma node-id=2033-1008)
import FAQSection from '../registration/subcomponents/FAQSection';
import Footer from './subcomponents/Footer';
import { clearSignupServiceTypeCookie } from '$utils/commonUtils';

interface ITranslations {
  common?: {
    getStarted?: string;
    registerNow?: string;
    knowMore?: string;
    learnMore?: string;
    callUs?: string;
    login?: string;
  };
  page?: {
    hero?: {
      badge?: string;
      title?: string;
      subtitle?: string;
    };
    stats?: {
      uniqueCustomers?: string;
      uniqueCustomersLabel?: string;
      marketShare?: string;
      marketShareLabel?: string;
      totalMarketValue?: string;
      totalMarketValueLabel?: string;
    };
    actionCards?: Array<{
      title?: string;
      description?: string;
      buttonText?: string;
      signupType?: string;
    }>;
  };
}

interface IAppProps {
  isMobile?: boolean;
  isDesktop?: boolean;
  currentLang?: string;
  translations?: ITranslations;
}

// Extend Window interface for Sentry
declare global {
  interface Window {
    Sentry?: {
      setTag: (key: string, value: string) => void;
    };
  }
}

const App: React.FC<IAppProps> = ({ isMobile = false, isDesktop = true, translations }) => {
  useEffect(() => {
    if (window.Sentry) {
      window.Sentry.setTag('page', 'Home');
    }
    clearSignupServiceTypeCookie();

    // BFCache: browser Back restores this tab without re-running JS mount; clear cookie then too.
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) {
        clearSignupServiceTypeCookie();
      }
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  // AI Generated: Extract translations with fallback defaults
  const t = translations?.page || {};
  const hero = t.hero || {};
  const stats = t.stats || {};
  const actionCards = t.actionCards || [];

  // AI Generated: Action card click handler for navigation
  const handleCardClick = useCallback((link: string, signupType?: string) => {
    if (signupType === 'NOVA') {
      document.cookie = 'signupServiceType=NOVA; path=/';
    } else {
      document.cookie = 'signupServiceType=BUS; path=/';
    }
    if (link.startsWith('http')) {
      window.open(link, '_blank', 'noopener,noreferrer');
    } else {
      window.location.href = link;
    }
  }, []);
  // AI Generated: End of click handler

  // Default action cards if not provided
  const defaultActionCards = [
    {
      title: 'Register your bus with redBus',
      description: 'Start selling on the world\'s largest bus ticketing platform today',
      buttonText: 'Register Now',
      icon: '/public/images/icons/register-bus.svg',
      alt: 'Register Bus',
      link: '/registration'
    },
    {
      title: 'Learn about bus business',
      description: 'Take the first step toward building your bus business with redBus',
      buttonText: 'Know More',
      icon: '/public/images/icons/learn-business.svg',
      alt: 'Learn Business',
      link: '/learn'
    },
    {
      title: 'Register as travel agent',
      description: 'Grow your business by booking tickets for your clients and earn commissions effortlessly',
      buttonText: 'Go to seatseller',
      icon: '/public/images/icons/travel-agent.svg',
      alt: 'Travel Agent',
      link: 'https://in3.seatseller.travel/'
    },
    {
      title: 'Register as NOVA vendor',
      description: 'Expand your business by selling to 8000+ bus operators across India',
      buttonText: 'Explore NOVA',
      icon: '/public/images/icons/explore-nova.svg',
      alt: 'Explore NOVA',
      link: '/registration',
      signupType: 'NOVA',
    },
  ];

  const cards = actionCards.length > 0 ? actionCards.map((card, i) => ({
    ...defaultActionCards[i],
    ...card
  })) : defaultActionCards;
  // AI Generated: End of translations extraction

  return (
    <div className={styles.homeContainer}>
      {/* Header */}
      <Header isMobile={isMobile} isDesktop={isDesktop} />

      {/* AI Generated: Hero Section - Figma node-id=1963-808 */}
      <section className={styles.heroSection}>
        <div className={styles.heroSection__background}>
          <img
            src="/public/images/hero/landing-page-hero.svg"
            alt="Bus Journey"
            className={styles.heroSection__backgroundImage}
          />
        </div>

        <div className={styles.heroSection__content}>
          {/* Left Content */}
          <div className={styles.heroSection__left}>
            <h1 className={styles.heroSection__title}>
              {hero.title || "Grow your business with the world's #1 bus platform"}
            </h1>
            <p className={styles.heroSection__subtitle}>
              {hero.subtitle || 'Get started with redBus now'}
            </p>
          </div>
        </div>

        {/* AI Generated: Arrow pointing to first action card - Figma node-id=2141:684 */}
        <img 
          src="/public/images/icons/arrow.svg" 
          alt="" 
          className={styles.heroSection__arrow}
        />
      </section>
      {/* AI Generated: End of Hero Section */}

      {/* AI Generated: Action Cards Section - Multi-language support */}
      <section className={styles.actionCards}>
        <div className={styles.actionCards__container}>
          {cards.map((card, index) => (
            <div key={index} className={styles.actionCard}>
              <div className={styles.actionCard__body}>
                <img
                  src={card.icon}
                  alt={card.alt}
                  className={styles.actionCard__icon}
                />
                <div className={styles.actionCard__content}>
                  <h3 className={styles.actionCard__title}>{card.title}</h3>
                  <p className={styles.actionCard__description}>{card.description}</p>
                </div>
              </div>
              <div className={styles.actionCard__cta}>
                <button 
                  className={styles.actionCard__button}
                  onClick={() => handleCardClick(card.link, card.signupType)}
                >
                  {card.buttonText}
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>
      {/* AI Generated: End of Action Cards Section */}

      {/* Platform Partners */}
      <PlatformPartners />

      {/* Capabilities */}
      <Capabilities />

      {/* Business Benefits */}
      <BusinessBenefits />

      {/* Testimonials */}
      <Testimonials />

      {/* Registration CTA */}
      <RegistrationCTA />

      {/* FAQ */}
      <FAQSection />

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default App;
// AI Generated: End of Bus Operator Onboarding Home Page
